import { List, ChevronRight, Download, FileText } from 'lucide-react';

/**
 * InteractiveMessage — preview visual de un mensaje interactivo de WhatsApp,
 * en el formato SALE (Gupshup v3) que produce core/chatbot_visual.py (motor de Funciones SAAS).
 *
 * RENDER PURO: recibe `message` (el shape v3) y lo dibuja tal como se verá al lead/vendedor.
 * Sin wiring, sin fetch, sin estado global → reusable en el preview del configurador (b)
 * y, después, en el display del chat del operador (a). Ante un shape desconocido no rompe (return null).
 */
export default function InteractiveMessage({ message }) {
  if (!message || typeof message !== 'object') return null;
  const type = message.type;

  // ── Texto (mensaje | pedir_dato) → body type=text ───────────────────
  if (type === 'text') {
    const body = message.text?.body;
    if (!body) return null;
    return (
      <div className="rounded-lg bg-black/40 border border-white/10 max-w-sm p-3">
        <p className="text-sm text-white/90 whitespace-pre-wrap">{body}</p>
      </div>
    );
  }

  // ── Media / descarga (image | video | document | audio, por link) ───
  if (type === 'image' || type === 'video' || type === 'document' || type === 'audio') {
    const media = message[type] || {};
    return (
      <div className="rounded-lg overflow-hidden bg-black/30 border border-emerald-500/20 max-w-sm">
        {type === 'image' && media.link && (
          <img src={media.link} alt={media.caption || ''} className="w-full max-h-64 object-cover" />
        )}
        {type === 'video' && media.link && (
          <video src={media.link} controls preload="metadata" className="w-full max-h-64 bg-black" />
        )}
        {type === 'audio' && media.link && (
          <div className="p-3"><audio src={media.link} controls className="w-full" /></div>
        )}
        {type === 'document' && (
          <div className="flex items-center gap-3 p-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white truncate">{media.filename || 'Documento'}</p>
              <p className="text-[10px] text-emerald-400 flex items-center gap-1">
                <Download className="w-3 h-3" /> Descargar
              </p>
            </div>
          </div>
        )}
        {media.caption && <p className="px-3 pb-2 text-xs text-white/80 whitespace-pre-wrap">{media.caption}</p>}
      </div>
    );
  }

  // ── Interactivo (list | button) ─────────────────────────────────────
  if (type === 'interactive' && message.interactive) {
    const ix = message.interactive;
    const header = ix.header?.text;
    const body = ix.body?.text;
    const footer = ix.footer?.text;
    return (
      <div className="rounded-lg bg-black/40 border border-white/10 max-w-sm overflow-hidden">
        <div className="p-3 space-y-1">
          {header && <p className="text-sm font-semibold text-white">{header}</p>}
          {body && <p className="text-sm text-white/90 whitespace-pre-wrap">{body}</p>}
          {footer && <p className="text-[10px] text-white/40">{footer}</p>}
        </div>

        {ix.type === 'list' && (
          <div className="border-t border-white/10">
            {/* Botón que abre la lista (como en WhatsApp) */}
            <div className="flex items-center justify-center gap-1.5 py-2 text-sky-400 text-sm font-medium">
              <List className="w-4 h-4" /> {ix.action?.button || 'Ver opciones'}
            </div>
            {/* Preview de las secciones y filas */}
            <div className="bg-black/20">
              {(ix.action?.sections || []).map((sec, si) => (
                <div key={si}>
                  {sec.title && (
                    <p className="px-3 pt-2 text-[10px] uppercase tracking-wide text-white/40">{sec.title}</p>
                  )}
                  {(sec.rows || []).map((row) => {
                    const esVerMas = typeof row.id === 'string' && row.id.startsWith('__mas__');
                    return (
                      <div
                        key={row.id}
                        className="flex items-center justify-between px-3 py-2 border-t border-white/5"
                      >
                        <div className="min-w-0">
                          <p className={`text-sm truncate ${esVerMas ? 'text-sky-400' : 'text-white'}`}>{row.title}</p>
                          {row.description && <p className="text-[11px] text-white/50 truncate">{row.description}</p>}
                        </div>
                        <ChevronRight className="w-4 h-4 text-white/30 shrink-0" />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {ix.type === 'button' && (
          <div className="border-t border-white/10 divide-y divide-white/5">
            {(ix.action?.buttons || []).map((b, bi) => (
              <div key={b.reply?.id || bi} className="py-2 text-center text-sky-400 text-sm font-medium">
                {b.reply?.title}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null; // shape desconocido → no rompe el chat
}
